import { useState } from "react";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import type { Container } from "@egalregal/shared";
import { ContainerMetaChips } from "../../shared/components/ContainerMetaChips";
import { useAppStore } from "../../shared/store/app";
import { useT } from "../../shared/i18n";
import { useDemoMode } from "../demo/useDemoMode";

/**
 * Trash (ticket 08): every soft-deleted container, with per-item restore and
 * a two-step confirmation for the destructive purge.
 */
export function TrashView() {
  const t = useT();
  const demoMode = useDemoMode();
  const trash = useAppStore((s) => s.trash);
  const restore = useAppStore((s) => s.restore);
  const purge = useAppStore((s) => s.purge);
  const [pendingPurge, setPendingPurge] = useState<Container | null>(null);

  return (
    <>
      {trash.length === 0 ? (
        <Typography variant="body1" color="text.secondary">
          {t("trashEmpty")}
        </Typography>
      ) : (
        <List>
          {trash.map((c) => (
            <ListItem key={c.id} disablePadding>
              <ListItemText
                primary={c.name}
                secondary={c.deletedAt ? `${t("deletedAt")}: ${new Date(c.deletedAt).toLocaleString()}` : undefined}
              />
              <ContainerMetaChips owner={c.owner} kind={c.kind} sx={{ ml: 1 }} />
              {!demoMode && (
                <Stack direction="row" spacing={1} sx={{ ml: 1 }}>
                  <Button size="small" onClick={() => void restore(c.id, c.version)}>
                    {t("restore")}
                  </Button>
                  <Button size="small" color="error" onClick={() => setPendingPurge(c)}>
                    {t("purge")}
                  </Button>
                </Stack>
              )}
            </ListItem>
          ))}
        </List>
      )}

      <Dialog
        open={!demoMode && pendingPurge !== null}
        onClose={() => setPendingPurge(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t("purgeConfirmTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{t("purgeConfirmBody")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingPurge(null)}>{t("cancel")}</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              if (pendingPurge) void purge([pendingPurge.id]);
              setPendingPurge(null);
            }}
          >
            {t("purge")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}